/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconProps,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useChain, useSpringRef } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import Router, { useRouter } from 'next/router';
import React, {
    FunctionComponent,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import SearchIcon from '@toucan/client/global/icons/search.svg';

import { MarketContext } from '@toucan/client/global/market';

import { scrollToTop } from '@toucan/client/core/scroll';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
} from '@toucan/client/shared/components/constrained-wrapper';
import { InputSize } from '@toucan/client/shared/components/input';
import { Link, LinkSize } from '@toucan/client/shared/components/link';
import {
    Typeahead,
    TypeaheadSuggestion,
    TypeaheadVariant,
} from '@toucan/client/shared/components/typeahead';
import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';
import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { useModelSuggestions } from '@toucan/client/blocks/hooks/use-model-suggestions';

import { useLocalStorage } from './hooks/use-local-storage';
import { styles } from './search-bar.styles';

export type SearchBarProps = {
    /**
     * If the component is open
     */
    isOpen: boolean;

    /**
     * The location of the search bar
     */
    location: SearchBarLocation;

    /**
     * A function thats called when the component should collapse
     */
    onCollapse?: () => void;

    /**
     * A function thats called when the input is focused
     */
    onFocus?: () => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Should the search bar allow its value to be cleared via button
     *
     * @default true
     */
    allowClear?: boolean;

    /**
     * Should the width of the searchbar be constrained?
     *
     * @default true
     */
    constrainWidth?: boolean;

    /**
     * Should the search bar steal focus on mount?
     *
     * @default true
     */
    stealFocus?: boolean;

    /**
     * An optional input size
     */
    inputSize?: InputSize;

    /**
     * Show the search icon at the end of the input
     *
     * @default false
     */
    showSuffixIcon?: boolean;
};

export enum SearchBarLocation {
    HOMEPAGE = 'HOMEPAGE',
    SITEWIDE = 'SITEWIDE',
}

export type StyledSearchBarProps = SearchBarProps & {
    hasSearchValue: boolean;
};

const StyledSearchBar = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const SearchButton = React.forwardRef<HTMLDivElement, IconProps>(
    (props: IconProps, ref) => (
        <Icon className="search-button" ref={ref} {...props}>
            <SearchIcon />
        </Icon>
    )
);

export const SearchBar: FunctionComponent<SearchBarProps> = ({
    isOpen,
    onCollapse,
    allowClear = true,
    constrainWidth = true,
    stealFocus = true,
    inputSize,
    location,
    onFocus,
    ...otherProps
}: SearchBarProps) => {
    const { t } = useTranslation();
    const router = useRouter();

    // Typeahead state
    const [searchValue, setSearchValue] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const { activeMarket } = useContext(MarketContext);
    const [recentSearches, setRecentSearches] = useLocalStorage(
        `${activeMarket.code}__RECENT_SEARCHES`,
        []
    );

    const {
        loading,
        typeaheadRef,
        fetchSuggestions,
        onStateChange,
        searchSuggestions,
    } = useModelSuggestions({
        highlightItemCallback: (index) => {
            setHighlightedIndex(index);
        },
    });

    const rootRef = useRef(
        typeof document !== 'undefined' ? document.documentElement : null
    );

    const searchBarRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const mobileSearchButtonRef = useRef<HTMLDivElement>(null);

    /**
     * Handle sending the user to search, by constructing a search URL
     * with their term. Also adds it to recent searches if required and ensures
     * that the searchValue in local state is reset.
     */
    function goToSearch(data?: TypeaheadSuggestion | null) {
        const rawTerm =
            searchSuggestions?.[highlightedIndex]?.term || searchValue;
        const encodedTerm = encodeURIComponent(rawTerm);

        // If there is a highlighted index but no search term, then we don't want to search for it
        if (~highlightedIndex && !encodedTerm) {
            return;
        }

        trackViaDataLayer({
            event: 'SEARCH_WIDGET_COMPLETE',
            searchTerm: rawTerm,
            location,
            typeAheadPosition: data?.meta?.index ?? -1,
        });

        Router.push(
            `/search?q=${encodedTerm}`,
            `/${activeMarket.slug}/${t('search', {
                ns: 'urls',
            })}?q=${encodedTerm}`
        ).then(() => {
            scrollToTop();
        });

        setHighlightedIndex(-1);
        onCollapse && onCollapse();
        inputRef?.current?.blur();

        let updatedRecentSearches;

        if (rawTerm.trim() === '') {
            return;
        }

        if (!recentSearches.includes(rawTerm)) {
            // Splice in rawTerm into existing recent searches and cap to 5
            updatedRecentSearches = [rawTerm, ...recentSearches].splice(0, 5);
        } else {
            // The term is in recent searches, ensure it now comes first
            updatedRecentSearches = recentSearches.sort(
                (a: string, b: string) => {
                    return a === rawTerm ? -1 : 1;
                }
            );
        }

        setRecentSearches(updatedRecentSearches);
    }

    /**
     * Handle a typeahead change by setting search value in local state
     * @param inputValue
     */
    function typeaheadInputChange(inputValue: string) {
        setSearchValue(inputValue);
    }

    /**
     * Handle clearing recent searches by setting them back to an empty array
     */
    function handleClearRecentSearches() {
        setRecentSearches([]);
        inputRef?.current?.focus();
    }

    const handleFocus = useCallback(() => {
        onFocus?.();
    }, [onFocus]);

    const handleOpenMobile = useCallback(() => {
        handleFocus();

        inputRef?.current?.focus();
    }, [handleFocus]);

    /**
     * Why are we binding this event listener here? Because on iOS the focus
     * can only be triggered by a user interaction, and React's synthetic events
     * don't cut it.
     */
    useEffect(() => {
        if (mobileSearchButtonRef?.current) {
            const mobileSearchButton = mobileSearchButtonRef?.current;

            mobileSearchButton.addEventListener('click', handleOpenMobile);

            return () => {
                mobileSearchButton.removeEventListener(
                    'click',
                    handleOpenMobile
                );
            };
        }

        return;
    }, [handleOpenMobile]);

    useClickOutside(searchBarRef, () => {
        isOpen && onCollapse && onCollapse();
        inputRef?.current?.blur();
        setHighlightedIndex(-1);
    });

    useKeyPress(rootRef.current, ['Escape', 'Esc'], () => {
        isOpen && onCollapse && onCollapse();
        inputRef?.current?.blur();
        setHighlightedIndex(-1);
    });

    useKeyPress(searchBarRef.current, ['Enter'], () => goToSearch());

    useEffect(() => {
        if (router.query.q) {
            try {
                const term = decodeURIComponent(router.query.q.toString());
                const displayTerm = term === '*' ? '' : term;
                setSearchValue(displayTerm);

                if (displayTerm) {
                    fetchSuggestions(displayTerm, Date.now().toString());
                }
            } catch (error) {
                setSearchValue('');
            }
        }
    }, [router.query.q]);

    const searchBarSpringRef = useSpringRef();
    const searchBarIconSpringRef = useSpringRef();

    const suggestionText = searchValue
        ? t('Suggestions')
        : t('Recent searches');

    const noSuggestionsText = searchValue
        ? t('No search suggestions')
        : undefined;

    const suggestions = searchValue
        ? searchSuggestions
        : recentSearches.map((term: string) => ({
              term,
          }));

    const typeaheadSuggestions: TypeaheadSuggestion[] = suggestions.map(
        (suggestion: TypeaheadSuggestion, index: number) => ({
            ...suggestion,
            meta: {
                index,
            },
        })
    );

    const ConstrainedWrapperElement = constrainWidth
        ? ConstrainedWrapper
        : 'div';

    const chainRefs = isOpen
        ? [searchBarSpringRef, searchBarIconSpringRef]
        : [searchBarIconSpringRef, searchBarSpringRef];

    useChain(chainRefs, isOpen ? [0, 0.5] : [0, 0]);

    const hasSearchValue = searchValue.length > 0;

    return (
        <StyledSearchBar
            isOpen={isOpen}
            hasSearchValue={hasSearchValue}
            location={location}
            {...otherProps}
        >
            <SearchButton
                data-testid="header__search-button-mobile"
                ref={mobileSearchButtonRef}
                variant={IconVariant.ACTIONABLE_SECONDARY}
                size={IconSize.L}
                isToggled={false}
            />
            <div
                className="search-bar"
                ref={searchBarRef}
                data-testid="search-bar__search-bar"
            >
                <ConstrainedWrapperElement
                    className="search-bar__wrapper"
                    gutter={ConstrainedWrapperGutter.LARGE_GUTTER}
                >
                    <div className="search-bar__inner">
                        <Typeahead
                            ref={typeaheadRef}
                            autoFocus={stealFocus}
                            variant={TypeaheadVariant.SEARCH_BAR}
                            suggestions={typeaheadSuggestions}
                            searchValue={searchValue}
                            label={t('Search for kit')}
                            placeholder={t('Search for kit')}
                            clearButtonLabel={
                                allowClear ? t('Close') : undefined
                            }
                            isLoading={loading}
                            showBorder={loading}
                            noSuggestionsText={noSuggestionsText}
                            suggestionsText={suggestionText}
                            onStateChange={onStateChange}
                            onInputValueChange={typeaheadInputChange}
                            onChange={goToSearch}
                            className="search-bar__typeahead"
                            inputRef={inputRef}
                            data-testid="search-bar__typeahead"
                            onClear={() => {
                                setSearchValue('');
                            }}
                            onFocus={handleFocus}
                            inputSize={inputSize}
                            suffixIcon={
                                <SearchButton
                                    data-testid="header__search-button"
                                    className="typeahead-search-button"
                                    variant={IconVariant.CORE}
                                    onClick={handleFocus}
                                />
                            }
                        >
                            {recentSearches.length > 0 && !searchValue && (
                                <Link
                                    onClick={handleClearRecentSearches}
                                    size={LinkSize.SMALL}
                                >
                                    {t('Clear recent searches')}
                                </Link>
                            )}
                        </Typeahead>
                    </div>
                </ConstrainedWrapperElement>
            </div>
        </StyledSearchBar>
    );
};
